import { useMemo } from "react";

import { useCompaniesCount } from "@/company/data/useCompaniesData";
import { FeatureTargeting } from "@reflag/shared/featureTargetingAPI";
import { reduce } from "@reflag/shared/filter";
import { targetingWithOptInMembershipsToFilter } from "@reflag/shared/utils/targetingRule";

import { useUsersCount } from "./useUsers";

export type TargetingEstimation = {
  companyCount: number | null;
  userCount: number | null;
};

/**
 * Hook to estimate targeting audience using companies/search and users endpoints.
 */
export function useTargetingEstimation(
  targeting: FeatureTargeting | undefined,
  flagKey: string,
  enabled = true,
  optInMemberships?: { userIds: string[]; companyIds: string[] },
) {
  const filter = useMemo(
    () =>
      targeting
        ? targetingWithOptInMembershipsToFilter(
            targeting,
            flagKey,
            optInMemberships,
          )
        : undefined,
    [targeting, flagKey, optInMemberships],
  );

  const queryEnabled = enabled && !!filter;

  const companiesQuery = useCompaniesCount(
    {
      filter: filter ? reduce(filter) : undefined,
    },
    queryEnabled,
  );

  const usersQuery = useUsersCount(
    {
      filter: filter ? reduce(filter) : undefined,
    },
    { enabled: queryEnabled },
  );

  const isLoading = companiesQuery.isLoading || usersQuery.isLoading;
  const isFetching = companiesQuery.isFetching || usersQuery.isFetching;
  const isSuccess =
    !queryEnabled || (companiesQuery.isSuccess && usersQuery.isSuccess);
  const isError = companiesQuery.isError || usersQuery.isError;
  const data: TargetingEstimation | undefined = isSuccess
    ? {
        companyCount: queryEnabled
          ? (companiesQuery.data?.totalCount ?? null)
          : 0,
        userCount: queryEnabled ? (usersQuery.data?.totalCount ?? null) : 0,
      }
    : undefined;

  return {
    data,
    isLoading,
    isFetching,
    isSuccess,
    isError,
  };
}
